import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { DangerSection } from "../Settings/DangerSection";
import { useToast } from "../Toast";

const CONFIRM_TITLE = "Reset this branch’s storage?";
const CONFIRM_DESCRIPTION =
  "This deletes every file this branch has stored. Live’s storage isn’t affected. Afterward you can copy live’s storage or start empty.";

function useResetBranchData(branchId: string) {
  const z = useZero();
  const { showToast } = useToast();
  return () => {
    void z
      .mutate(sharedMutators.resetBranchVolumeData({ branchId }))
      .server.then((result) => {
        if (result.type === "error") {
          showToast({
            icon: "circle-off",
            message: result.error.message,
            buttonLabel: "Dismiss",
          });
        }
      });
  };
}

export function ResetBranchDataButton({
  branchId,
  disabled = false,
}: {
  branchId: string;
  disabled?: boolean;
}) {
  const resetBranchData = useResetBranchData(branchId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (disabled && confirmOpen) setConfirmOpen(false);

  const reset = () => {
    if (disabled) return;
    setConfirmOpen(false);
    resetBranchData();
  };

  return (
    <>
      <Button
        size="l"
        variant="secondary"
        icon="rotate-ccw"
        disabled={disabled}
        onClick={() => setConfirmOpen(true)}
      >
        Reset storage
      </Button>
      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={reset}
        title={CONFIRM_TITLE}
        description={CONFIRM_DESCRIPTION}
        confirmLabel="Reset"
        destructive
      />
    </>
  );
}

export function ResetBranchDataSection({ branchId }: { branchId: string }) {
  const resetBranchData = useResetBranchData(branchId);
  return (
    <DangerSection
      title="Reset storage"
      description="Deletes every file this branch has stored. Live’s storage isn’t affected."
      actionLabel="Reset storage"
      confirm={{
        title: CONFIRM_TITLE,
        description: CONFIRM_DESCRIPTION,
        confirmLabel: "Reset",
        onConfirm: resetBranchData,
      }}
    />
  );
}
